import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { autoriaDoUsuarioLogado } from '../utils/recebimentoAutoria';
import { useDateFormatter } from './useDateFormatter';
import { validate } from '../contracts/validate';
import '../contracts/Recebimentos_SaveInput.ts';
import '../contracts/Recebimentos_GetAvailableForPaymentRulesInput.ts';
import '../contracts/Recebimentos_CarregaRecebimentoResponse.ts';
import '../contracts/Recebimentos_DeleteResponse.ts';
import '../contracts/Recebimentos_GetrecebimentosResponse.ts';
import '../contracts/Recebimentos_GetrecebimentoscomoperadorResponse.ts';
import '../contracts/Recebimentos_CarregaRecebimentoResponse';
import '../contracts/Recebimentos_DeleteResponse';
import '../contracts/Recebimentos_GetavailablerecebimentosforpaymentrulesResponse';
import '../contracts/Recebimentos_GetrecebimentosbyownerandowneridResponse';
import '../contracts/Recebimentos_GetrecebimentoscomoperadorResponse';
import '../contracts/Recebimentos_GetrecebimentosResponse';
import '../contracts/Recebimentos_SaveResponse';

import '../contracts/Contas_GetContasDisponiveisResponse';
import '../contracts/SubaccountsLinks_PendingRefundsResponse';
import '../contracts/SubaccountsLinks_RefundRecebimentoResponse';
import '../contracts/SubaccountsLinks_RefundRecebimentoInput';
import type { SubaccountsLinks_PendingRefundsResponseDados } from '../contracts/SubaccountsLinks_PendingRefundsResponse';
import type { SubaccountsLinks_RefundRecebimentoInput } from '../contracts/SubaccountsLinks_RefundRecebimentoInput';
import type { Recebimentos_SaveInput } from '../contracts/Recebimentos_SaveInput';
import type { Recebimentos_GetAvailableForPaymentRulesInput } from '../contracts/Recebimentos_GetAvailableForPaymentRulesInput';
/**
 * Composable for Recebimento (Receivable/Payment) operations
 * Provides methods to interact with the Recebimentos API
 *
 * TYPED DATA LAYER: This composable implements the Typed Data Layer pattern.
 * - DATE-ONLY fields: Use fromDateToApiDate (YYYY-MM-DD, no timezone conversion)
 * - DATETIME fields: Use toApiDateTimeFormat/fromApiDateTimeFormat (ISO 8601 UTC)
 * Date-only fields represent calendar dates, not moments in time.
 * - See: .serena/memories/timezone-typed-data-layer.md
 */
export function useRecebimento() {
  const isLoading = ref(false);
  const error = ref(null);

  // Initialize date formatter (Typed Data Layer)
  // DATE-ONLY: fromDateToApiDate (no timezone conversion)
  // DATETIME: toApiDateTimeFormat/fromApiDateTimeFormat (UTC conversion)
  const {
    fromDateToApiDate,
    toApiDateTimeFormat
  } = useDateFormatter();

  /**
   * Fetch all recebimentos (filtered by date and franchise on backend)
   */
  const fetchRecebimentos = async () => {
    log('useRecebimento', 'fetchRecebimentos', 'Fetching all recebimentos');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/recebimentos/getRecebimentos/', {
        responseContract: 'Recebimentos_GetrecebimentosResponse',
      });
      log('useRecebimento', 'fetchRecebimentos', 'Response', response);

      if (response.success) {
        return response.dados?.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar recebimentos');
      }
    } catch (err) {
      log('useRecebimento', 'fetchRecebimentos', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch recebimentos by generic origem type and ID
   */
  const fetchRecebimentosByOrigem = async (origem: string, origemId: number) => {
    log('useRecebimento', 'fetchRecebimentosByOrigem', 'Fetching recebimentos', { origem, origemId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/recebimentos/getRecebimentosByOwnerAndOwnerID/${origem}/${origemId}`, {
        responseContract: 'Recebimentos_GetrecebimentosbyownerandowneridResponse',
      });
      log('useRecebimento', 'fetchRecebimentosByOrigem', 'Response', response);

      if (response.success) {
        return response.dados || [];
      }
      return [];
    } catch (err) {
      log('useRecebimento', 'fetchRecebimentosByOrigem', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch recebimentos for a specific compromisso
   */
  const fetchRecebimentosByCompromisso = async (compromissoId: number) => {
    return fetchRecebimentosByOrigem('compromisso', compromissoId);
  };

  /**
   * Fetch single recebimento by ID
   */
  const fetchRecebimento = async (recebimentoId: number) => {
    log('useRecebimento', 'fetchRecebimento', 'Fetching recebimento', recebimentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/recebimentos/carrega_recebimento/${recebimentoId}`, {
        responseContract: 'Recebimentos_CarregaRecebimentoResponse'
      });
      log('useRecebimento', 'fetchRecebimento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Recebimento não encontrado');
      }
    } catch (err) {
      log('useRecebimento', 'fetchRecebimento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create new recebimento
   */
  const createRecebimento = async (data: Recebimentos_SaveInput) => {
    log('useRecebimento', 'createRecebimento', 'Creating recebimento', data);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Recebimentos_SaveInput', data);
      const response = await httpService.post('/index.php/recebimentos/save/', data, {
        inputContract: 'Recebimentos_SaveInput',
        responseContract: 'Recebimentos_SaveResponse'
      });
      log('useRecebimento', 'createRecebimento', 'Response', response);

      if (response.success) {
        // O caixa NÃO é lançado daqui. O app-api lança a venda em dinheiro ao salvar o
        // recebimento (recebimentos/save, forma CASH). Um segundo lançamento feito pela tela
        // dobrava o dinheiro, mandava centavos para um campo em reais e punha cartão e Pix
        // como dinheiro esperado no fechamento diário — chamado New Shoes 2026-0065.
        return response;
      } else {
        throw new Error(response.error || 'Erro ao criar recebimento');
      }
    } catch (err) {
      log('useRecebimento', 'createRecebimento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create multiple recebimentos in bulk from compromissos
   *
   * Each recebimento is created with:
   * - Payment method from compromisso (compromisso_forma_pagamento)
   * - Value as the remaining amount (valor_restante)
   * - Today's date
   *
   * @param {Array} compromissos - Array of compromisso objects with payment data
   * @returns {Object} Result with success count, errors array, and created recebimentos
   */
  const createBulkRecebimentos = async (compromissos: any) => {
    log('useRecebimento', 'createBulkRecebimentos', 'Creating bulk recebimentos', { count: compromissos.length });
    isLoading.value = true;
    error.value = null;

    const results: any = {
      success: [],
      errors: [],
      total: compromissos.length
    };

    // Get today's date in API format (Typed Data Layer - date-only, no TZ conversion)
    const today = fromDateToApiDate(new Date());

    // Get user info for registrado_por
    const { permissionsService } = await import('../services/permissionsService');
    const userId = permissionsService.getUser()?.sub || '';

    try {
      // Process each compromisso sequentially to avoid overwhelming the server
      for (const compromisso of compromissos) {
        // Skip compromissos that are already fully paid (status 2)
        if (Number(compromisso.compromisso_status_financeiro) === 2) {
          log('useRecebimento', 'createBulkRecebimentos', 'Skipping fully paid compromisso', compromisso.compromisso_id);
          results.errors.push({
            compromisso_id: compromisso.compromisso_id,
            error: 'Compromisso já está totalmente pago'
          });
          continue;
        }

        // Calculate remaining value (valor_restante is in reais, convert to centavos)
        const valorRestante = compromisso.valor_restante || 0;
        if (valorRestante <= 0) {
          log('useRecebimento', 'createBulkRecebimentos', 'Skipping compromisso with no remaining value', compromisso.compromisso_id);
          results.errors.push({
            compromisso_id: compromisso.compromisso_id,
            error: 'Não há valor restante a receber'
          });
          continue;
        }

        // Prepare recebimento data
        // Use compromisso date as default, fallback to today if not available
        // Handle various date formats: Date object, ISO string, datetime string, or date string
        // Typed Data Layer: Convert to API format
        let compromissoDate = today;
        const rawDate = compromisso.compromisso_data;
        if (rawDate) {
          if (rawDate instanceof Date) {
            // Already a Date object - convert to API format (date-only, no TZ conversion)
            compromissoDate = fromDateToApiDate(rawDate);
          } else {
            // String format - extract date part (handles "YYYY-MM-DD", "YYYY-MM-DD HH:MM:SS", "YYYY-MM-DDTHH:MM:SS")
            const dateStr = String(rawDate);
            compromissoDate = dateStr.split(' ')[0].split('T')[0];
          }
        }

        const recebimentoData = {
          recebimento_id: 0,
          recebimento_forma_pagamento_id: compromisso.compromisso_forma_pagamento || null,
          recebimento_valor: Math.round(valorRestante * 100), // Gross value in centavos (what customer paid)
          recebimento_valor_taxas: 0, // Fee value in centavos (default 0)
          recebimento_valor_liquido: Math.round(valorRestante * 100), // Net value in centavos (will be calculated by backend)
          recebimento_observacoes: '',
          recebimento_franquia_id: compromisso.compromisso_franquia_id,
          recebimento_origem: 'compromisso',
          recebimento_origem_id: compromisso.compromisso_id,
          ...autoriaDoUsuarioLogado(),
          recebimento_data_cadastro: toApiDateTimeFormat(new Date()),
          recebimento_data: compromissoDate
        };

        log('useRecebimento', 'createBulkRecebimentos', 'Creating recebimento for compromisso', {
          compromisso_id: compromisso.compromisso_id,
          valor: valorRestante,
          forma_pagamento: recebimentoData.recebimento_forma_pagamento_id,
          recebimento_data: compromissoDate
        });

        try {
          const response = await createRecebimento(recebimentoData);
          results.success.push({
            compromisso_id: compromisso.compromisso_id,
            recebimento: response.dados
          });
        } catch (err) {
          log('useRecebimento', 'createBulkRecebimentos', 'Error creating recebimento for compromisso', compromisso.compromisso_id, err);
          results.errors.push({
            compromisso_id: compromisso.compromisso_id,
            error: err.message || 'Erro desconhecido'
          });
        }
      }

      log('useRecebimento', 'createBulkRecebimentos', 'Bulk creation complete', {
        successCount: results.success.length,
        errorCount: results.errors.length
      });

      return results;
    } catch (err) {
      log('useRecebimento', 'createBulkRecebimentos', 'Critical error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update existing recebimento
   */
  const updateRecebimento = async (data: Recebimentos_SaveInput) => {
    log('useRecebimento', 'updateRecebimento', 'Updating recebimento', data);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Recebimentos_SaveInput', data);
      const response = await httpService.post('/index.php/recebimentos/save/', data, {
        inputContract: 'Recebimentos_SaveInput',
        responseContract: 'Recebimentos_SaveResponse'
      });
      log('useRecebimento', 'updateRecebimento', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao atualizar recebimento');
      }
    } catch (err) {
      log('useRecebimento', 'updateRecebimento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete recebimento
   */
  const deleteRecebimento = async (recebimentoId: number) => {
    log('useRecebimento', 'deleteRecebimento', 'Deleting recebimento', recebimentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/recebimentos/delete/${recebimentoId}`, {
        responseContract: 'Recebimentos_DeleteResponse'
      });
      log('useRecebimento', 'deleteRecebimento', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao excluir recebimento');
      }
    } catch (err) {
      log('useRecebimento', 'deleteRecebimento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch available contas for a franchise
   */
  const fetchContasDisponiveis = async (franquiaId: number) => {
    log('useRecebimento', 'fetchContasDisponiveis', 'Fetching contas for franquia', franquiaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/contas/contas/get_contas_disponiveis/${franquiaId}`, null, { inputContract: 'Contas_GetContasDisponiveisInput', responseContract: 'Contas_GetContasDisponiveisResponse' });
      log('useRecebimento', 'fetchContasDisponiveis', 'Response', response);

      if (response.success) {
        return response.dados || {};
      } else {
        throw new Error(response.error || 'Erro ao buscar contas disponíveis');
      }
    } catch (err) {
      log('useRecebimento', 'fetchContasDisponiveis', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };


  /**
   * Fetch compromissos with payment status information
   *
   * OPTIMIZED: The backend already returns all payment data via lista_compromissos:
   * - compromisso_status_financeiro (numeric: 0=Pendente, 1=Parcial, 2=Pago)
   * - status_financeiro (translated string)
   * - valor_recebido (amount paid in centavos)
   * - total (total value)
   *
   * This eliminates the N+1 query problem where we previously fetched
   * recebimentos for each compromisso individually.
   *
   * @param {Object} filters - Filter options for compromissos
   * @param {Object} options - Additional options
   * @param {Object} options.queryOverride - Override session filter (see httpService.buildQueryOverrideHeaders)
   * @param {Array<number|string>} options.queryOverride.franquiaIds - Franchise IDs
   * @param {string} options.queryOverride.dataInicio - Start date (Y-m-d)
   * @param {string} options.queryOverride.dataFim - End date (Y-m-d)
   * @param {Array<string>} [options.queryOverride.userIds] - Optional user subs
   * @returns {Array} Compromissos with normalized payment data
   */
  const fetchCompromissosWithPaymentStatus = async (filters = {}, options = {}) => {
    log('useRecebimento', 'fetchCompromissosWithPaymentStatus', 'Fetching compromissos with payment status (optimized)', { filters, options });
    isLoading.value = true;
    error.value = null;

    try {
      // Import useCompromisso dynamically to avoid circular dependency
      const { useCompromisso } = await import('./useCompromisso');
      const { fetchCompromissosList } = useCompromisso();

      // Fetch all compromissos - backend already includes payment status data
      // Pass queryOverride if provided to bypass session filter
      const compromissos = await fetchCompromissosList(filters, options);
      log('useRecebimento', 'fetchCompromissosWithPaymentStatus', 'Fetched compromissos', compromissos.length);

      // Fetch payment methods for enrichment
      const { useFormasPagamento } = await import('./useFormasPagamento');
      const { listarFormasPagamento } = useFormasPagamento();
      let formasPagamentoMap = {};
      try {
        const formasPagamento = await listarFormasPagamento();
        log('useRecebimento', 'fetchCompromissosWithPaymentStatus', 'Fetched formas de pagamento', formasPagamento.length);
        // Create lookup map for payment method names
        formasPagamento.forEach((fp: any) => {
          (formasPagamentoMap as any)[fp.forma_pagamento_id] = fp.forma_pagamento_nome;
        });
      } catch (fpErr) {
        log('useRecebimento', 'fetchCompromissosWithPaymentStatus', 'Warning: Could not fetch formas de pagamento', fpErr);
        // Continue without payment method names - non-critical
      }

      // Normalize field names for frontend consumption
      const normalizedData = compromissos.map((c: any) => {
        // Backend returns valor_recebido (gross/faturado) in centavos, convert to reais
        const valorPago = (parseFloat(c.valor_recebido) || 0) / 100;

        // Backend returns valor_liquido (net) in centavos, convert to reais
        const valorLiquido = (parseFloat(c.valor_liquido) || 0) / 100;

        // Handle different field names from API
        const valorTotal = parseFloat(c.compromisso_valor_total || c.total || 0);
        const valorRestante = valorTotal - valorPago;

        // Normalize field names for frontend consistency
        const compromissoData = c.compromisso_data || c.compromisso_data_hora_inicio || '';
        const clienteNome = c.cliente_nome || c.nome_cliente || '';

        // Get payment method name from lookup
        const formaPagamentoId = c.compromisso_forma_pagamento || c.forma_pagamento_id || null;
        const formaPagamentoNome = formaPagamentoId ? ((formasPagamentoMap as any)[formaPagamentoId] || null) : null;

        return {
          ...c,
          // Normalized fields for frontend
          compromisso_data: compromissoData,
          cliente_nome: clienteNome,
          compromisso_valor_total: valorTotal,
          valor_pago: valorPago,              // Faturado (gross amount received)
          valor_liquido: valorLiquido,        // Líquido (net amount after fees)
          valor_restante: valorRestante > 0 ? valorRestante : 0,
          // Payment method enrichment
          forma_pagamento_id: formaPagamentoId,
          forma_pagamento_nome: formaPagamentoNome,
          // Backend already provides these:
          // compromisso_status_financeiro: numeric ID (0, 1, 2)
          // status_financeiro: translated string from backend
          recebimentos_count: null // Not available without additional query, but not used in UI
        };
      });

      log('useRecebimento', 'fetchCompromissosWithPaymentStatus', 'Normalized data with payment methods', normalizedData.length);
      return normalizedData;
    } catch (err) {
      log('useRecebimento', 'fetchCompromissosWithPaymentStatus', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Calculate dashboard metrics from compromissos with payment data
   * @param {Array} compromissos - Array of compromissos with payment status
   * @returns {Object} Dashboard metrics
   */
  const calculateDashboardMetrics = (compromissos: any) => {
    log('useRecebimento', 'calculateDashboardMetrics', 'Calculating metrics for', compromissos.length, 'compromissos');

    const metrics = {
      // Total metrics
      total: compromissos.length,
      totalValor: compromissos.reduce((sum: any, c: any) => sum + (c.compromisso_valor_total || 0), 0),

      // Complete: paid >= total (status 2)
      complete: compromissos.filter((c: any) => Number(c.compromisso_status_financeiro) === 2).length,
      completeValor: compromissos
        .filter((c: any) => Number(c.compromisso_status_financeiro) === 2)
        .reduce((sum: any, c: any) => sum + (c.valor_pago || 0), 0),

      // Partial: 0 < paid < total (status 1)
      partial: compromissos.filter((c: any) => Number(c.compromisso_status_financeiro) === 1).length,
      partialValor: compromissos
        .filter((c: any) => Number(c.compromisso_status_financeiro) === 1)
        .reduce((sum: any, c: any) => sum + (c.valor_pago || 0), 0),

      // Due: paid == 0 (status 0)
      due: compromissos.filter((c: any) => Number(c.compromisso_status_financeiro) === 0).length,
      dueValor: compromissos
        .filter((c: any) => Number(c.compromisso_status_financeiro) === 0)
        .reduce((sum: any, c: any) => sum + (c.compromisso_valor_total || 0), 0)
    };

    log('useRecebimento', 'calculateDashboardMetrics', 'Metrics', metrics);
    return metrics;
  };

  /**
   * Fetch recebimentos with payment method and operator information
   * @returns {Array} Recebimentos enriched with forma de pagamento and operador data
   */
  const fetchRecebimentosWithPaymentMethodAndOperator = async () => {
    log('useRecebimento', 'fetchRecebimentosWithPaymentMethodAndOperator', 'Fetching recebimentos with payment method and operator');
    isLoading.value = true;
    error.value = null;

    try {
      // Fetch recebimentos with compromisso user data (JOIN)
      const response = await httpService.get('/index.php/recebimentos/getRecebimentosComOperador/', {
        responseContract: 'Recebimentos_GetrecebimentoscomoperadorResponse',
      });
      log('useRecebimento', 'fetchRecebimentosWithPaymentMethodAndOperator', 'API response', response);

      const recebimentos = response.dados?.dados || [];
      log('useRecebimento', 'fetchRecebimentosWithPaymentMethodAndOperador', 'Fetched recebimentos', recebimentos.length);

      // Fetch formas de pagamento lookup
      const { useFormasPagamento } = await import('./useFormasPagamento');
      const { listarFormasPagamento } = useFormasPagamento();
      const formasPagamento = await listarFormasPagamento();
      log('useRecebimento', 'fetchRecebimentosWithPaymentMethodAndOperador', 'Fetched formas de pagamento', formasPagamento.length);

      // Create lookup map for formas de pagamento
      const formasPagamentoMap: any = {};
      formasPagamento.forEach((fp: any) => {
        formasPagamentoMap[fp.forma_pagamento_id] = fp.forma_pagamento_nome;
      });

      // Fetch users lookup
      const { useUsers } = await import('./useUsers');
      const { fetchUsers } = useUsers();
      const usersResult = await fetchUsers();
      const users: any[] = Array.isArray(usersResult) ? usersResult : [];
      log('useRecebimento', 'fetchRecebimentosWithPaymentMethodAndOperador', 'Fetched users', users.length);

      // Create lookup map for users
      const usersMap: any = {};
      users.forEach((user: any) => {
        // Map both by sub (AWS Cognito ID) and user_id
        usersMap[user.sub] = user.name || user.email || user.username || 'Desconhecido';
        if (user.user_id) {
          usersMap[user.user_id] = user.name || user.email || user.username || 'Desconhecido';
        }
      });

      // Enrich each recebimento with parsed data
      const enrichedData = recebimentos.map((r: any) => {
        // Convert valor from centavos to reais
        // recebimento_valor = gross value (what customer paid)
        // recebimento_valor_taxas = fees
        // recebimento_valor_liquido = net value (valor - taxas)
        const valorReais = (r.recebimento_valor || 0) / 100;
        const valorTaxasReais = (r.recebimento_valor_taxas || 0) / 100;
        const valorLiquidoReais = (r.recebimento_valor_liquido || (r.recebimento_valor - (r.recebimento_valor_taxas || 0)) || 0) / 100;

        // Get forma de pagamento name from lookup
        const formaPagamentoId = r.recebimento_forma_pagamento_id || 0;
        const formaPagamentoNome = formasPagamentoMap[formaPagamentoId] || 'Sem forma de pagamento';

        // Get operador name from compromisso (who performed the service)
        const operadorId = r.compromisso_user_id || '';
        const operadorNome = usersMap[operadorId] || 'Sem operador';

        // Get registrado por name (who registered the payment)
        const registradoPorId = r.recebimento_registrado_por_id || '';
        const registradoPorNome = usersMap[registradoPorId] || 'Desconhecido';

        return {
          ...r,
          recebimento_valor_display: valorReais,
          recebimento_valor_taxas_display: valorTaxasReais,
          recebimento_valor_liquido_display: valorLiquidoReais,
          forma_pagamento_nome: formaPagamentoNome,
          forma_pagamento_id: formaPagamentoId,
          operador_nome: operadorNome,
          operador_id: operadorId,
          registrado_por_nome: registradoPorNome,
          registrado_por_id: registradoPorId
        };
      });

      log('useRecebimento', 'fetchRecebimentosWithPaymentMethodAndOperator', 'Enriched data', enrichedData.length);
      return enrichedData;
    } catch (err) {
      log('useRecebimento', 'fetchRecebimentosWithPaymentMethodAndOperator', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch available recebimentos for payroll period by PAYMENT RULES
   *
   * ╔════════════════════════════════════════════════════════════════════════════╗
   * ║                    HYBRID ARCHITECTURE METHOD                              ║
   * ╠════════════════════════════════════════════════════════════════════════════╣
   * ║                                                                            ║
   * ║  This method implements the HYBRID approach for recebimento claiming:      ║
   * ║                                                                            ║
   * ║  KEY BUSINESS RULE:                                                        ║
   * ║  → A recebimento can be claimed by MULTIPLE payment rules                  ║
   * ║    (e.g., Kevin's 30% operator commission + João's 5% seller commission)   ║
   * ║  → BUT only ONCE per payment rule (across ALL payroll periods)             ║
   * ║                                                                            ║
   * ║  WHY payment_rule_ids (not execution_ids)?                                 ║
   * ║  → The business constraint is at the RULE level, not execution level       ║
   * ║  → Junction table has UNIQUE(payment_rule_id, recebimento_id)              ║
   * ║  → Different executions of the SAME rule cannot claim the same recebimento ║
   * ║                                                                            ║
   * ║  EXAMPLE:                                                                  ║
   * ║  - Payroll has 2 rules: Kevin (rule_id=1) + João (rule_id=2)               ║
   * ║  - API call: paymentRuleIds: [1, 2]                                        ║
   * ║  - Returns: recebimentos NOT yet claimed by rule 1 OR rule 2               ║
   * ║                                                                            ║
   * ╚════════════════════════════════════════════════════════════════════════════╝
   *
   * Used by: Revenue Conciliation Wizard (Step 3)
   *
   * @param {number} franchiseId - Franchise ID
   * @param {string} startDate - Start date (Y-m-d format)
   * @param {string} endDate - End date (Y-m-d format)
   * @param {Array<number>} paymentRuleIds - Array of payment rule IDs to check against
   * @param {Array<string>} userIds - Optional array of Cognito subs to filter by operator/seller
   * @returns {Array} Available recebimentos enriched with forma de pagamento and operador data
   */
  const fetchAvailableRecebimentosForPaymentRules = async (franchiseId: any, startDate: any, endDate: any, paymentRuleIds = [], userIds = []) => {
    log('useRecebimento', 'fetchAvailableRecebimentosForPaymentRules', 'Fetching available recebimentos by payment rules', { franchiseId, startDate, endDate, paymentRuleIds, userIds });
    isLoading.value = true;
    error.value = null;

    try {
      // Format dates to Y-m-d using Typed Data Layer (date-only, no TZ conversion)
      const formatDateParam = (date: any) => {
        if (!date) return null;
        if (typeof date === 'string') return date;
        return fromDateToApiDate(date);
      };

      const formattedStartDate = formatDateParam(startDate);
      const formattedEndDate = formatDateParam(endDate);

      // Enforce required dates before building the typed payload
      if (!formattedStartDate) {
        throw new Error('Recebimentos_GetAvailableForPaymentRulesInput validation failed:\n  - "start_date" is required');
      }
      if (!formattedEndDate) {
        throw new Error('Recebimentos_GetAvailableForPaymentRulesInput validation failed:\n  - "end_date" is required');
      }

      // Build and validate payload
      const payload: Recebimentos_GetAvailableForPaymentRulesInput = {
        franchise_id: franchiseId,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        payment_rule_ids: paymentRuleIds,
        user_ids: userIds
      };
      validate('Recebimentos_GetAvailableForPaymentRulesInput', payload);

      // Fetch available recebimentos using the new hybrid architecture endpoint
      const response = await httpService.post(
        '/index.php/recebimentos/getAvailableRecebimentosForPaymentRules',
        payload,
        { inputContract: 'Recebimentos_GetAvailableForPaymentRulesInput', responseContract: 'Recebimentos_GetavailablerecebimentosforpaymentrulesResponse' }
      );
      log('useRecebimento', 'fetchAvailableRecebimentosForPaymentRules', 'API response', response);

      const recebimentos = response.dados || [];
      log('useRecebimento', 'fetchAvailableRecebimentosForPaymentRules', 'Fetched available recebimentos', recebimentos.length);

      // Fetch formas de pagamento lookup
      const { useFormasPagamento } = await import('./useFormasPagamento');
      const { listarFormasPagamento } = useFormasPagamento();
      const formasPagamento = await listarFormasPagamento();
      log('useRecebimento', 'fetchAvailableRecebimentosForPaymentRules', 'Fetched formas de pagamento', formasPagamento.length);

      // Create lookup map for formas de pagamento
      const formasPagamentoMap: any = {};
      formasPagamento.forEach((fp: any) => {
        formasPagamentoMap[fp.forma_pagamento_id] = fp.forma_pagamento_nome;
      });

      // Fetch users lookup
      const { useUsers } = await import('./useUsers');
      const { fetchUsers } = useUsers();
      const usersResult = await fetchUsers();
      const users: any[] = Array.isArray(usersResult) ? usersResult : [];
      log('useRecebimento', 'fetchAvailableRecebimentosForPaymentRules', 'Fetched users', users.length);

      // Create lookup map for users
      const usersMap: any = {};
      users.forEach((user: any) => {
        usersMap[user.sub] = user.name || user.email || user.username || 'Desconhecido';
        if (user.user_id) {
          usersMap[user.user_id] = user.name || user.email || user.username || 'Desconhecido';
        }
      });

      // Enrich each recebimento with parsed data
      const enrichedData = recebimentos.map((r: any) => {
        // Convert valor from centavos to reais
        // recebimento_valor = gross value (what customer paid)
        // recebimento_valor_taxas = fees
        // recebimento_valor_liquido = net value (valor - taxas)
        const valorReais = (r.recebimento_valor || 0) / 100;
        const valorTaxasReais = (r.recebimento_valor_taxas || 0) / 100;
        const valorLiquidoReais = (r.recebimento_valor_liquido || (r.recebimento_valor - (r.recebimento_valor_taxas || 0)) || 0) / 100;

        // Get forma de pagamento name from lookup
        const formaPagamentoId = r.recebimento_forma_pagamento_id || 0;
        const formaPagamentoNome = formasPagamentoMap[formaPagamentoId] || 'Sem forma de pagamento';

        // Get operador name from compromisso (who performed the service)
        const operadorId = r.compromisso_user_id || '';
        const operadorNome = (usersMap as any)[operadorId] || 'Sem operador';

        // Get orcamento user name (who recorded the orcamento)
        const orcamentoUserId = r.orcamento_user_id || '';
        const orcamentoUserNome = (usersMap as any)[orcamentoUserId] || '';

        // Get registrado por name (who registered the payment)
        const registradoPorId = r.recebimento_registrado_por_id || '';
        const registradoPorNome = (usersMap as any)[registradoPorId] || 'Desconhecido';

        return {
          ...r,
          recebimento_valor_display: valorReais,
          recebimento_valor_taxas_display: valorTaxasReais,
          recebimento_valor_liquido_display: valorLiquidoReais,
          forma_pagamento_nome: formaPagamentoNome,
          forma_pagamento_id: formaPagamentoId,
          operador_nome: operadorNome,
          operador_id: operadorId,
          orcamento_user_nome: orcamentoUserNome,
          orcamento_user_id: orcamentoUserId,
          registrado_por_nome: registradoPorNome,
          registrado_por_id: registradoPorId
        };
      });

      log('useRecebimento', 'fetchAvailableRecebimentosForPaymentRules', 'Enriched data', enrichedData.length);
      return enrichedData;
    } catch (err) {
      log('useRecebimento', 'fetchAvailableRecebimentosForPaymentRules', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Calculate metrics by operador and payment method
   * @param {Array} recebimentos - Array of recebimentos with payment method and operador data
   * @returns {Object} Metrics grouped by operador and payment method
   */
  const calculateOperadorPaymentMethodMetrics = (recebimentos: any) => {
    log('useRecebimento', 'calculateOperadorPaymentMethodMetrics', 'Calculating metrics for', recebimentos.length, 'recebimentos');

    // Group by operador
    const operadorMap: any = {};

    recebimentos.forEach((r: any) => {
      const operadorId = r.operador_id || 'unknown';
      const operadorNome = r.operador_nome || 'Desconhecido';
      const formaPagamentoId = r.forma_pagamento_id || 0;
      const formaPagamentoNome = r.forma_pagamento_nome || 'Sem forma de pagamento';
      const valor = r.recebimento_valor_display || 0;

      // Initialize operador if not exists
      if (!operadorMap[operadorId]) {
        operadorMap[operadorId] = {
          operadorId,
          operadorNome,
          totalRecebido: 0,
          totalRecebimentos: 0,
          paymentMethodsMap: {}
        };
      }

      // Add to operador totals
      operadorMap[operadorId].totalRecebido += valor;
      operadorMap[operadorId].totalRecebimentos += 1;

      // Initialize payment method if not exists
      if (!operadorMap[operadorId].paymentMethodsMap[formaPagamentoId]) {
        operadorMap[operadorId].paymentMethodsMap[formaPagamentoId] = {
          formaPagamentoId,
          formaPagamentoNome,
          valor: 0,
          count: 0
        };
      }

      // Add to payment method totals
      operadorMap[operadorId].paymentMethodsMap[formaPagamentoId].valor += valor;
      operadorMap[operadorId].paymentMethodsMap[formaPagamentoId].count += 1;
    });

    // Convert to array and sort
    const operadores = Object.values(operadorMap).map((operador: any) => ({
      ...operador,
      paymentMethods: Object.values(operador.paymentMethodsMap).sort((a: any, b: any) => b.valor - a.valor)
    })).sort((a: any, b: any) => b.totalRecebido - a.totalRecebido);

    // Remove paymentMethodsMap from final output
    operadores.forEach(op => delete op.paymentMethodsMap);

    // Calculate totals
    const totals: any = {
      totalCount: recebimentos.length,
      totalValor: recebimentos.reduce((sum: any, r: any) => sum + (r.recebimento_valor_display || 0), 0)
    };

    log('useRecebimento', 'calculateOperadorPaymentMethodMetrics', 'Metrics', { operadores: operadores.length, totals });
    return { operadores, totals };
  };

  // ============================================
  // CSV IMPORT HELPERS
  // ============================================

  /**
   * Parse CSV value string to number (Brazilian format)
   * Handles: "1.234,56" or "1234.56" or "R$ 1.234,56"
   * @param {string} valueStr - The value string from CSV
   * @returns {Object} { value: number, isPositive: boolean }
   */
  const parseCsvValue = (valueStr: any) => {
    if (!valueStr || typeof valueStr !== 'string') {
      return { value: 0, isPositive: false };
    }

    // Remove currency symbols, whitespace
    let cleaned = valueStr.replace(/[R$\s]/g, '').trim();

    // Handle negative sign
    const isNegative = cleaned.startsWith('-') || cleaned.startsWith('(');
    if (isNegative) {
      cleaned = cleaned.replace(/[-()]/g, '');
    }

    // Detect format: Brazilian (1.234,56) vs US (1,234.56)
    // If last separator is comma and has 2 digits after, it's Brazilian
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    let value = 0;

    if (lastComma > lastDot && cleaned.length - lastComma === 3) {
      // Brazilian format: 1.234,56
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      value = parseFloat(cleaned) || 0;
    } else if (lastDot > lastComma && cleaned.length - lastDot === 3) {
      // US format: 1,234.56
      cleaned = cleaned.replace(/,/g, '');
      value = parseFloat(cleaned) || 0;
    } else {
      // Simple number or ambiguous - try to parse
      cleaned = cleaned.replace(/,/g, '.');
      value = parseFloat(cleaned) || 0;
    }

    return {
      value: Math.abs(value),
      isPositive: !isNegative && value !== 0
    };
  };

  /**
   * Parse date string from CSV (multiple formats)
   * @param {string} dateStr - Date string from CSV
   * @returns {string|null} Date in YYYY-MM-DD format or null
   */
  const parseCsvDate = (dateStr: any) => {
    if (!dateStr || typeof dateStr !== 'string') {
      return null;
    }

    const cleaned = dateStr.trim();

    // Try multiple formats
    const formats = [
      // DD/MM/YYYY
      { regex: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, order: [3, 2, 1] },
      // DD-MM-YYYY
      { regex: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, order: [3, 2, 1] },
      // DD.MM.YYYY
      { regex: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, order: [3, 2, 1] },
      // YYYY-MM-DD
      { regex: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, order: [1, 2, 3] },
      // YYYY/MM/DD
      { regex: /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, order: [1, 2, 3] },
      // DD/MM/YY (2-digit year)
      { regex: /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/, order: [3, 2, 1], shortYear: true },
    ];

    for (const format of formats) {
      const match = cleaned.match(format.regex);
      if (match) {
        let year = match[format.order[0]];
        const month = match[format.order[1]].padStart(2, '0');
        const day = match[format.order[2]].padStart(2, '0');

        // Handle 2-digit year
        if (format.shortYear) {
          const yearNum = parseInt(year, 10);
          year = yearNum > 50 ? `19${year}` : `20${year.padStart(2, '0')}`;
        }

        // Validate date
        const dateObj = new Date(`${year}-${month}-${day}`);
        if (!isNaN(dateObj.getTime())) {
          return `${year}-${month}-${day}`;
        }
      }
    }

    return null;
  };

  /**
   * Normalize string for comparison (remove accents, lowercase, trim)
   */
  const normalizeString = (str: any) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s]/g, '') // Keep only alphanumeric and spaces
      .trim();
  };

  /**
   * Calculate name similarity score between two strings
   * Returns a value between 0 and 1
   */
  const calculateNameSimilarity = (str1: any, str2: any) => {
    const s1 = normalizeString(str1);
    const s2 = normalizeString(str2);

    if (!s1 || !s2) return 0;

    // Exact match
    if (s1 === s2) return 1.0;

    // One contains the other completely
    if (s1.includes(s2) || s2.includes(s1)) {
      const shorter = s1.length < s2.length ? s1 : s2;
      const longer = s1.length < s2.length ? s2 : s1;
      return shorter.length / longer.length * 0.95; // Up to 95% for contains
    }

    // Word-based matching
    const words1 = s1.split(/\s+/).filter((w: any) => w.length > 2);
    const words2 = s2.split(/\s+/).filter((w: any) => w.length > 2);

    if (words1.length === 0 || words2.length === 0) return 0;

    // Count matching words
    let matchingWords = 0;
    for (const w1 of words1 as any[]) {
      for (const w2 of words2 as any[]) {
        if (w1 === w2 || w1.includes(w2) || w2.includes(w1)) {
          matchingWords++;
          break;
        }
      }
    }

    const maxWords = Math.max(words1.length, words2.length);
    return (matchingWords / maxWords) * 0.85; // Up to 85% for word overlap
  };

  /**
   * Calculate match score between CSV row and compromisso
   *
   * Score weights (in order of importance):
   * - Name match: up to 150 points (highest - almost no chance of error)
   * - Value match: up to 100 points
   * - Date match: up to 50 points
   * - Status bonus: up to 10 points
   *
   * @param {Object} csvRow - { date: 'YYYY-MM-DD', value: number, description: string }
   * @param {Object} compromisso - from fetchCompromissosWithPaymentStatus
   * @param {Object} options - { includePaid: false, compareWithTotal: false }
   * @returns {Object} { score: number, breakdown: { name, value, date, status } }
   */
  const calculateMatchScore = (csvRow: any, compromisso: any, options: any = {}) => {
    const { includePaid = false, compareWithTotal = false }: any = options;
    const breakdown = { name: 0, value: 0, date: 0, status: 0 };

    // Skip fully paid compromissos unless explicitly including them
    if (!includePaid && Number(compromisso.compromisso_status_financeiro) === 2) {
      return { score: 0, breakdown };
    }

    // 1. NAME SCORING (HIGHEST WEIGHT - up to 150 points)
    // Compare CSV description with cliente_nome
    const csvDescription = csvRow.description || '';
    const clienteNome = compromisso.cliente_nome || '';

    if (csvDescription && clienteNome) {
      const similarity = calculateNameSimilarity(csvDescription, clienteNome);

      if (similarity >= 0.95) {        // Near exact match
        breakdown.name = 150;
      } else if (similarity >= 0.80) { // Very high similarity
        breakdown.name = 120;
      } else if (similarity >= 0.60) { // Good similarity
        breakdown.name = 80;
      } else if (similarity >= 0.40) { // Moderate similarity
        breakdown.name = 40;
      } else if (similarity >= 0.25) { // Some word overlap
        breakdown.name = 20;
      }
    }

    // 2. Value scoring - up to 100 points
    // For paid compromissos, compare with total value; for unpaid, compare with remaining
    const csvValue = csvRow.value;
    const targetValue = compareWithTotal
      ? (compromisso.compromisso_valor_total || 0)
      : (compromisso.valor_restante || 0);

    if (targetValue > 0 && csvValue > 0) {
      const valueDiff = Math.abs(csvValue - targetValue) / targetValue;

      if (valueDiff <= 0.005) {        // ±0.5% = exact
        breakdown.value = 100;
      } else if (valueDiff <= 0.05) {  // ±5% = near
        breakdown.value = 70;
      } else if (valueDiff <= 0.10) {  // ±10% = loose
        breakdown.value = 40;
      } else if (valueDiff <= 0.20) {  // ±20% = weak
        breakdown.value = 15;
      }
    }

    // 3. Date scoring - up to 50 points
    if (csvRow.date && compromisso.compromisso_data) {
      const csvDate = new Date(csvRow.date);
      const compDateStr = compromisso.compromisso_data.split(' ')[0]; // Handle datetime
      const compDate = new Date(compDateStr);

      if (!isNaN(csvDate.getTime()) && !isNaN(compDate.getTime())) {
        const daysDiff = Math.abs(((csvDate as any) - (compDate as any)) / (1000 * 60 * 60 * 24));

        if (daysDiff === 0) {
          breakdown.date = 50;
        } else if (daysDiff <= 1) {
          breakdown.date = 40;
        } else if (daysDiff <= 3) {
          breakdown.date = 25;
        } else if (daysDiff <= 7) {
          breakdown.date = 10;
        } else if (daysDiff <= 14) {
          breakdown.date = 5;
        }
      }
    }

    // 4. Status bonus (prefer pending over partial) - up to 10 points
    const status = Number(compromisso.compromisso_status_financeiro);
    if (status === 0) {        // Pendente
      breakdown.status = 10;
    } else if (status === 1) { // Parcial
      breakdown.status = 5;
    }

    const score = breakdown.name + breakdown.value + breakdown.date + breakdown.status;
    return { score, breakdown };
  };

  /**
   * Get confidence level from score
   * @param {number} score - Match score
   * @param {Object} breakdown - Score breakdown { name, value, date, status }
   * @returns {string} 'EXACT' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE'
   *
   * Score weights:
   * - Name: up to 150 (highest - names matching = almost no error)
   * - Value: up to 100
   * - Date: up to 50
   * - Status: up to 10
   * - Max possible: 310
   */
  const getConfidenceLevel = (score: any, breakdown: any = null) => {
    if (breakdown) {
      // EXACT: High name match (>=120) - names matching means almost no chance of error
      // OR: Exact value (100) AND close date (>=40) - traditional exact match
      if (breakdown.name >= 120 || (breakdown.value === 100 && breakdown.date >= 40)) {
        return 'EXACT';
      }
    }
    if (score >= 180) return 'HIGH';    // Good name + value OR excellent overall
    if (score >= 100) return 'MEDIUM';  // Moderate match
    if (score >= 40) return 'LOW';      // Some match
    return 'NONE';                       // No meaningful match
  };

  /**
   * Find best compromisso matches for a CSV row
   * @param {Object} csvRow - { date, value, description }
   * @param {Array} compromissos - Available compromissos
   * @param {Object} options - { topN: 3, includePaid: false, onlyPaid: false }
   * @returns {Array} [{ compromisso, score, confidence, breakdown }]
   */
  const findCompromissoMatches = (csvRow: any, compromissos: any, options: any = {}) => {
    const { topN = 3, includePaid = false, onlyPaid = false }: any = options;
    log('useRecebimento', 'findCompromissoMatches', 'Finding matches for', { csvRow, includePaid, onlyPaid });

    // Filter compromissos based on payment status
    let filteredCompromissos = compromissos;
    if (onlyPaid) {
      // Only paid compromissos
      filteredCompromissos = compromissos.filter((c: any) => Number(c.compromisso_status_financeiro) === 2);
    } else if (!includePaid) {
      // Exclude paid compromissos (default)
      filteredCompromissos = compromissos.filter((c: any) => Number(c.compromisso_status_financeiro) !== 2);
    }
    // If includePaid is true and onlyPaid is false, include all

    const matches: any[] = filteredCompromissos
      .map((compromisso: any) => {
        // For paid compromissos, compare with total value
        const isPaid = Number(compromisso.compromisso_status_financeiro) === 2;
        const { score, breakdown } = calculateMatchScore(csvRow, compromisso, {
          includePaid: true,
          compareWithTotal: isPaid
        });
        return {
          compromisso,
          score,
          breakdown,
          confidence: getConfidenceLevel(score, breakdown)
        };
      })
      .filter((m: any) => m.score > 0)
      .sort((a: any, b: any) => b.score - a.score)
      .slice(0, topN);

    log('useRecebimento', 'findCompromissoMatches', 'Found matches', matches.length);
    return matches;
  };

  /**
   * Process all CSV rows and find matches using two-phase matching:
   *
   * PHASE 1: Match against PAID compromissos first
   *   - If a CSV row has EXACT/HIGH match with a paid compromisso, mark as "already_conciliated"
   *   - This detects rows that were already imported in a previous run
   *
   * PHASE 2: Match remaining rows against UNPAID compromissos
   *   - Uses greedy algorithm to ensure each compromisso is only assigned once
   *
   * @param {Array} csvRows - Array of { date, value, description }
   * @param {Array} compromissos - Available compromissos (all statuses)
   * @returns {Array} Rows with matches attached
   */
  const processAllCsvRows = (csvRows: any, compromissos: any) => {
    log('useRecebimento', 'processAllCsvRows', 'Processing rows with two-phase matching', csvRows.length);

    // Initialize all rows
    const rowsWithAllMatches = csvRows.map((row: any, index: any) => ({
      ...row,
      rowIndex: index,
      matches: [],
      paidMatches: [], // Matches against paid compromissos
      selectedMatch: null,
      selectedMatchId: '',
      confidence: 'NONE',
      status: row.isPositive ? 'unmatched' : 'skipped'
    }));

    // ===========================================
    // PHASE 1: Check against PAID compromissos
    // ===========================================
    log('useRecebimento', 'processAllCsvRows', 'PHASE 1: Checking against PAID compromissos');

    const alreadyConciliatedRows = new Set();

    rowsWithAllMatches.forEach((row: any) => {
      if (row.status === 'skipped') return;

      // Find matches against PAID compromissos only
      const paidMatches = findCompromissoMatches(row, compromissos, { topN: 3, onlyPaid: true });
      row.paidMatches = paidMatches;

      // If there's a high-confidence match with a paid compromisso,
      // this row was likely already imported
      if (paidMatches.length > 0) {
        const bestPaidMatch = paidMatches[0];
        // Only mark as already_conciliated if confidence is EXACT or HIGH
        if (bestPaidMatch.confidence === 'EXACT' || bestPaidMatch.confidence === 'HIGH') {
          row.status = 'already_conciliated';
          row.selectedMatch = bestPaidMatch;
          row.confidence = bestPaidMatch.confidence;
          alreadyConciliatedRows.add(row.rowIndex);
          log('useRecebimento', 'processAllCsvRows', 'Row already conciliated', {
            rowIndex: row.rowIndex,
            compromissoId: bestPaidMatch.compromisso.compromisso_id,
            confidence: bestPaidMatch.confidence
          });
        }
      }
    });

    log('useRecebimento', 'processAllCsvRows', 'PHASE 1 complete', {
      alreadyConciliated: alreadyConciliatedRows.size
    });

    // ===========================================
    // PHASE 2: Match remaining rows against UNPAID compromissos
    // ===========================================
    log('useRecebimento', 'processAllCsvRows', 'PHASE 2: Matching against UNPAID compromissos');

    // Find matches for rows that are not already conciliated
    rowsWithAllMatches.forEach((row: any) => {
      if (row.status === 'skipped' || row.status === 'already_conciliated') return;

      // Find matches against UNPAID compromissos only
      row.matches = findCompromissoMatches(row, compromissos, { topN: 10, onlyPaid: false });
    });

    // Build combinations for greedy assignment (only for non-conciliated rows)
    const allCombinations: any = [];
    rowsWithAllMatches.forEach((row: any) => {
      if (row.status === 'skipped' || row.status === 'already_conciliated') return;

      row.matches.forEach((match: any) => {
        allCombinations.push({
          rowIndex: row.rowIndex,
          compromissoId: match.compromisso.compromisso_id,
          match,
          score: match.score
        });
      });
    });

    // Sort by score descending (best matches first)
    allCombinations.sort((a: any, b: any) => b.score - a.score);

    // Greedy assignment - assign each compromisso only once
    const assignedCompromissos = new Set();
    const assignedRows = new Set();

    allCombinations.forEach((combo: any) => {
      if (assignedCompromissos.has(combo.compromissoId) || assignedRows.has(combo.rowIndex)) {
        return;
      }

      const row = rowsWithAllMatches[combo.rowIndex];
      row.selectedMatch = combo.match;
      row.confidence = combo.match.confidence;
      row.status = 'matched';

      assignedCompromissos.add(combo.compromissoId);
      assignedRows.add(combo.rowIndex);
    });

    // Filter matches to only show non-assigned compromissos
    rowsWithAllMatches.forEach((row: any) => {
      if (row.status === 'skipped' || row.status === 'already_conciliated') return;

      const selectedId = row.selectedMatch?.compromisso?.compromisso_id;
      row.matches = row.matches.filter((m: any) => {
        const compId = m.compromisso.compromisso_id;
        return compId === selectedId || !assignedCompromissos.has(compId);
      });
    });

    log('useRecebimento', 'processAllCsvRows', 'PHASE 2 complete', {
      matched: assignedRows.size,
      alreadyConciliated: alreadyConciliatedRows.size
    });

    return rowsWithAllMatches;
  };

  /**
   * Create recebimentos from matched CSV rows
   * @param {Array} matchedRows - Rows with selectedMatch
   * @param {Object} options - { formaPagamentoId, franquiaId }
   * @returns {Object} { success: [], errors: [], total: number }
   */
  const createRecebimentosFromCsv = async (matchedRows: any, options: any = {}) => {
    log('useRecebimento', 'createRecebimentosFromCsv', 'Creating recebimentos', {
      rows: matchedRows.length,
      options
    });

    isLoading.value = true;
    error.value = null;

    const results: { success: any[]; errors: any[]; total: number } = {
      success: [],
      errors: [],
      total: matchedRows.length
    };

    // Get user info for registrado_por
    const { permissionsService } = await import('../services/permissionsService');
    const userId = permissionsService.getUser()?.sub || '';

    // Get today's date in API format (Typed Data Layer - date-only, no TZ conversion)
    const today = fromDateToApiDate(new Date());

    for (const row of matchedRows) {
      // Skip rows without a selected match
      if (!row.selectedMatch?.compromisso) {
        results.errors.push({
          row: row.rowIndex,
          error: 'Nenhum compromisso selecionado',
          csvData: row
        });
        continue;
      }

      const compromisso = row.selectedMatch.compromisso;
      const value = row.value;

      // Prepare recebimento data
      const recebimentoData = {
        recebimento_id: 0,
        recebimento_forma_pagamento_id: options.formaPagamentoId || compromisso.compromisso_forma_pagamento || null,
        recebimento_valor: Math.round(value * 100), // Gross value in centavos (what customer paid)
        recebimento_valor_taxas: 0, // Fee value in centavos (default 0 for CSV imports)
        recebimento_valor_liquido: Math.round(value * 100), // Net value in centavos (will be calculated by backend)
        recebimento_observacoes: row.description || `Importado do CSV - ${row.originalValue || ''}`,
        recebimento_franquia_id: options.franquiaId || compromisso.compromisso_franquia_id,
        recebimento_origem: 'compromisso',
        recebimento_origem_id: compromisso.compromisso_id,
        ...autoriaDoUsuarioLogado(),
        recebimento_data_cadastro: toApiDateTimeFormat(new Date()),
        recebimento_data: row.date || today
      };

      try {
        const response = await createRecebimento(recebimentoData);
        results.success.push({
          row: row.rowIndex,
          compromisso_id: compromisso.compromisso_id,
          recebimento: response.dados,
          value
        });
      } catch (err) {
        log('useRecebimento', 'createRecebimentosFromCsv', 'Error creating', err);
        results.errors.push({
          row: row.rowIndex,
          compromisso_id: compromisso.compromisso_id,
          error: err.message || 'Erro desconhecido',
          csvData: row
        });
      }
    }

    log('useRecebimento', 'createRecebimentosFromCsv', 'Complete', {
      success: results.success.length,
      errors: results.errors.length
    });

    isLoading.value = false;
    return results;
  };

  // ============================================
  // REFUND OPERATIONS
  // ============================================

  /**
   * Check if a recebimento can be refunded via payment provider
   *
   * A recebimento is eligible for refund if:
   * - It has a provider charge ID (recebimento_id_externo is not empty)
   * - The charge ID indicates it was processed by a payment provider
   *
   * @param {Object} recebimento - Recebimento object
   * @returns {boolean} True if the recebimento can be refunded
   *
   * @example
   * const { canRefundRecebimento } = useRecebimento();
   * if (canRefundRecebimento(recebimento)) {
   *   // Show refund button
   * }
   */
  const canRefundRecebimento = (recebimento: any) => {
    if (!recebimento) return false;

    const chargeId = recebimento.recebimento_id_externo;

    // Must have a non-empty charge ID from the provider
    return chargeId !== null &&
           chargeId !== undefined &&
           chargeId !== '' &&
           String(chargeId).trim() !== '';
  };

  /**
   * Check if a recebimento has a pending refund request
   *
   * A pending refund means the user has requested a refund but the payment
   * provider (e.g., ASAAS) has not yet confirmed it via webhook.
   *
   * @param {number} recebimentoId - Recebimento ID
   * @returns {Object} { hasPending: boolean, pendingRequest: object|null, allRequests: array }
   *
   * @example
   * const { getPendingRefundStatus } = useRecebimento();
   * const status = await getPendingRefundStatus(123);
   * if (status.hasPending) {
   *   log('RecebimentoForm', 'checkPendingRefund', 'Pending refund:', status.pendingRequest);
   * }
   */
  const getPendingRefundStatus = async (recebimentoId: number) => {
    log('useRecebimento', 'getPendingRefundStatus', 'Checking pending refund', recebimentoId);

    // Default safe response
    const safeDefault = { has_pending: false, pending_request: null, all_requests: [] };

    if (!recebimentoId) {
      log('useRecebimento', 'getPendingRefundStatus', 'No recebimentoId provided');
      return safeDefault;
    }

    try {
      const response = await httpService.get(
        `/index.php/subaccounts/links/pending_refunds/${recebimentoId}`,
        { responseContract: 'SubaccountsLinks_PendingRefundsResponse' }
      );
      log('useRecebimento', 'getPendingRefundStatus', 'Raw response', response);

      // Check for successful response
      if (response && response.success === true) {
        const dados: SubaccountsLinks_PendingRefundsResponseDados | undefined = response.dados;

        // Validate the response structure
        if (dados && typeof dados === 'object') {
          log('useRecebimento', 'getPendingRefundStatus', 'Valid dados', dados);
          return {
            has_pending: dados.has_pending === true,
            pending_request: dados.pending_request || null,
            all_requests: dados.all_requests || []
          };
        }
      }

      // If we get here, response was not successful or invalid
      log('useRecebimento', 'getPendingRefundStatus', 'Invalid or unsuccessful response, returning default');
      return safeDefault;
    } catch (err) {
      log('useRecebimento', 'getPendingRefundStatus', 'Error', err);
      // Don't throw - return safe default
      return safeDefault;
    }
  };

  /**
   * Check if a recebimento has a pending refund (quick check)
   *
   * Lightweight version that just returns boolean.
   * Use getPendingRefundStatus() if you need the full pending request details.
   *
   * @param {number} recebimentoId - Recebimento ID
   * @returns {boolean} True if there's a pending refund request
   */
  const hasPendingRefund = async (recebimentoId: number) => {
    const status = await getPendingRefundStatus(recebimentoId);
    log('useRecebimento', 'hasPendingRefund', 'Status received', {
      recebimentoId,
      status,
      hasPending: (status as any).hasPending,
      has_pending: status.has_pending
    });
    // Use strict equality to avoid truthy/falsy issues
    return (status as any).hasPending === true || status.has_pending === true;
  };

  /**
   * Format pending refund for display
   *
   * @param {Object} pendingRequest - Pending request object from API
   * @returns {Object} { amount: string, date: string, timeAgo: string }
   */
  const formatPendingRefund = (pendingRequest: any) => {
    if (!pendingRequest) return null;

    const amount = pendingRequest.requested_amount
      ? (pendingRequest.requested_amount / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : 'R$ 0,00';

    const requestedAt = pendingRequest.requested_at;
    let date = '';
    let timeAgo = '';

    if (requestedAt) {
      const dateObj = new Date(requestedAt);
      date = dateObj.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      // Calculate time ago
      const now = new Date();
      const diffMs = (now as any) - (dateObj as any);
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffDays > 0) {
        timeAgo = `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
      } else if (diffHours > 0) {
        timeAgo = `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
      } else if (diffMins > 0) {
        timeAgo = `há ${diffMins} minuto${diffMins > 1 ? 's' : ''}`;
      } else {
        timeAgo = 'agora mesmo';
      }
    }

    return {
      amount,
      date,
      timeAgo,
      description: pendingRequest.description || ''
    };
  };

  /**
   * Refund a specific recebimento/charge via payment provider (e.g., ASAAS)
   *
   * This is the CORRECT way to refund payments made through payment links.
   * It uses the charge ID (stored in recebimento_id_externo) rather than
   * the payment link ID.
   *
   * ASAAS Terminology:
   * ==================
   * - Payment Link (link_xxx): Public URL for payment - stored in payment_links.provider_link_id
   * - Charge (pay_xxx): Actual payment/transaction - stored in contas_recebimentos.recebimento_id_externo
   *
   * Why recebimento_id_externo?
   * ===========================
   * A single payment link can generate MULTIPLE charges (e.g., installments,
   * multiple customers paying through the same link). To refund a specific
   * payment, you MUST use the charge ID, which is stored in recebimento_id_externo.
   *
   * The backend will:
   * 1. Look up the recebimento to get the charge ID from recebimento_id_externo
   * 2. Find the associated payment_link via payment_links_recebimentos junction
   * 3. Use the subaccount's API key to call the provider's refund API
   * 4. Record the refund in payment link history
   * 5. Update the recebimento status
   *
   * @param {number} recebimentoId - Recebimento ID (from contas_recebimentos table)
   * @param {Object} options - Refund options
   * @param {number} options.value - Amount to refund (optional, full refund if not specified)
   * @param {string} options.description - Reason for the refund (optional)
   * @returns {Object} Refund result with charge_id, provider_response, etc.
   *
   * @example
   * // Full refund
   * const result = await refundRecebimento(123);
   *
   * @example
   * // Partial refund with description
   * const result = await refundRecebimento(123, {
   *   value: 50.00,
   *   description: 'Partial refund - service not completed'
   * });
   */
  const refundRecebimento = async (recebimentoId: any, options: any = {}) => {
    log('useRecebimento', 'refundRecebimento', 'Refunding recebimento', { recebimentoId, options });
    isLoading.value = true;
    error.value = null;

    try {
      const data: SubaccountsLinks_RefundRecebimentoInput = {};
      if (options.value !== undefined && options.value !== null) {
        data.value = options.value;
      }
      if (options.description) {
        data.description = options.description;
      }

      const response = await httpService.post(
        `/index.php/subaccounts/links/refund_recebimento/${recebimentoId}`,
        data,
        { inputContract: 'SubaccountsLinks_RefundRecebimentoInput', responseContract: 'SubaccountsLinks_RefundRecebimentoResponse' }
      );
      log('useRecebimento', 'refundRecebimento', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao estornar pagamento');
      }
    } catch (err) {
      log('useRecebimento', 'refundRecebimento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Calculate metrics for Recebíveis dashboard
   * Provides both "Available" and "Received" perspectives
   *
   * @param {Array} recebimentos - Array of recebimento objects
   * @returns {Object} - Metrics object with availability and receipt date breakdowns
   */
  const calculateRecebiveisMetrics = (recebimentos: any) => {
    log('useRecebimento', 'calculateRecebiveisMetrics', 'Calculating metrics for', recebimentos.length, 'recebimentos');

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (7 - today.getDay())); // Sunday

    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    // Helper to parse date string to Date object
    const parseDate = (dateStr: any) => {
      if (!dateStr) return null;
      const date = new Date(dateStr);
      date.setHours(0, 0, 0, 0);
      return date;
    };

    // Helper to get value from recebimento (values are always in centavos)
    const getValue = (rec: any) => {
      const valor = parseFloat(rec.recebimento_valor) || 0;
      return valor / 100;
    };

    // Initialize metrics
    const metrics = {
      // Total metrics
      total: recebimentos.length,
      totalValor: 0,

      // Availability metrics (based on recebimento_data_disponivel)
      available: {
        today: { count: 0, valor: 0 },
        thisWeek: { count: 0, valor: 0 },
        thisMonth: { count: 0, valor: 0 },
        pending: { count: 0, valor: 0 }, // Future dates
        total: { count: 0, valor: 0 }
      },

      // Receipt date metrics (based on recebimento_data)
      received: {
        today: { count: 0, valor: 0 },
        thisWeek: { count: 0, valor: 0 },
        thisMonth: { count: 0, valor: 0 },
        total: { count: 0, valor: 0 }
      },

      // Source metrics
      bySource: {
        provider: { count: 0, valor: 0 }, // ASAAS, etc
        manual: { count: 0, valor: 0 }
      },

      // Payment method metrics
      byPaymentMethod: {}
    };

    recebimentos.forEach((rec: any) => {
      const valor = getValue(rec);
      metrics.totalValor += valor;

      const dataDisponivel = parseDate(rec.recebimento_data_disponivel);
      const dataRecebimento = parseDate(rec.recebimento_data);

      // Availability metrics (based on data_disponivel)
      if (dataDisponivel) {
        metrics.available.total.count++;
        metrics.available.total.valor += valor;

        if (dataDisponivel <= today) {
          metrics.available.today.count++;
          metrics.available.today.valor += valor;
        }
        if (dataDisponivel <= endOfWeek) {
          metrics.available.thisWeek.count++;
          metrics.available.thisWeek.valor += valor;
        }
        if (dataDisponivel <= endOfMonth) {
          metrics.available.thisMonth.count++;
          metrics.available.thisMonth.valor += valor;
        }
        if (dataDisponivel > today) {
          metrics.available.pending.count++;
          metrics.available.pending.valor += valor;
        }
      } else {
        // If no data_disponivel, assume available immediately (manual entries)
        metrics.available.today.count++;
        metrics.available.today.valor += valor;
        metrics.available.thisWeek.count++;
        metrics.available.thisWeek.valor += valor;
        metrics.available.thisMonth.count++;
        metrics.available.thisMonth.valor += valor;
        metrics.available.total.count++;
        metrics.available.total.valor += valor;
      }

      // Receipt date metrics (based on recebimento_data)
      if (dataRecebimento) {
        metrics.received.total.count++;
        metrics.received.total.valor += valor;

        if (dataRecebimento.toDateString() === today.toDateString()) {
          metrics.received.today.count++;
          metrics.received.today.valor += valor;
        }
        if (dataRecebimento >= today && dataRecebimento <= endOfWeek) {
          metrics.received.thisWeek.count++;
          metrics.received.thisWeek.valor += valor;
        }
        if (dataRecebimento.getMonth() === today.getMonth() &&
            dataRecebimento.getFullYear() === today.getFullYear()) {
          metrics.received.thisMonth.count++;
          metrics.received.thisMonth.valor += valor;
        }
      }

      // Source metrics (provider vs manual)
      const registradoPor = (rec.recebimento_registrado_por || '').toLowerCase();
      if (registradoPor === 'manual' || !registradoPor) {
        metrics.bySource.manual.count++;
        metrics.bySource.manual.valor += valor;
      } else {
        metrics.bySource.provider.count++;
        metrics.bySource.provider.valor += valor;
      }

      // Payment method metrics
      const formaPagamento = rec.forma_pagamento_nome || rec.recebimento_forma_pagamento || 'Não informado';
      if (!(metrics.byPaymentMethod as any)[formaPagamento]) {
        (metrics.byPaymentMethod as any)[formaPagamento] = { count: 0, valor: 0 };
      }
      (metrics.byPaymentMethod as any)[formaPagamento].count++;
      (metrics.byPaymentMethod as any)[formaPagamento].valor += valor;
    });

    log('useRecebimento', 'calculateRecebiveisMetrics', 'Metrics calculated', metrics);
    return metrics;
  };

  return {
    // State
    isLoading,
    error,

    // CRUD operations
    fetchRecebimentos,
    fetchRecebimentosByOrigem,
    fetchRecebimentosByCompromisso,
    fetchRecebimento,
    createRecebimento,
    createBulkRecebimentos,
    updateRecebimento,
    deleteRecebimento,

    // Helper data
    fetchContasDisponiveis,

    // Payment tracking
    fetchCompromissosWithPaymentStatus,
    calculateDashboardMetrics,
    calculateRecebiveisMetrics,

    // Payment method and operator analysis
    fetchRecebimentosWithPaymentMethodAndOperator,
    fetchAvailableRecebimentosForPaymentRules,
    calculateOperadorPaymentMethodMetrics,

    // CSV import helpers
    parseCsvValue,
    parseCsvDate,
    calculateMatchScore,
    getConfidenceLevel,
    findCompromissoMatches,
    processAllCsvRows,
    createRecebimentosFromCsv,

    // Refund operations
    canRefundRecebimento,
    refundRecebimento,
    getPendingRefundStatus,
    hasPendingRefund,
    formatPendingRefund
  };
}
